// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
// Assignment Detail Page — Moodle-like grading interface
import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { assignmentAPI, submissionAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  ArrowLeft, 
  Upload, 
  FileText, 
  Clock, 
  Calendar,
  Loader2,
  Download,
  CheckCircle2,
  XCircle,
  User,
  Users,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  AlertTriangle,
  FileCheck,
  Save,
  SkipForward,
  RotateCcw,
  Search,
  Mail,
  Trash2,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link2,
  Bell,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatDateTime, getDaysUntil, getInitials } from "@/lib/utils";
import "./AssignmentGrading.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============ Simple Rich Text Editor for Feedback ============
function FeedbackEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const [initialized, setInitialized] = useState(false);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (editorRef.current && !initialized) {
      editorRef.current.innerHTML = value || "";
      setInitialized(true);
    }
  }, [value, initialized]);

  // Reset only when value changes externally (navigation between students)
  // Skip when the change came from user typing (isInternalChange)
  useEffect(() => {
    if (editorRef.current && initialized) {
      if (isInternalChange.current) {
        isInternalChange.current = false;
        return;
      }
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  const execCmd = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    handleInput();
  };

  const handleInput = () => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  };

  const insertLink = () => {
    const url = prompt("URL du lien :");
    if (url) execCmd("createLink", url);
  };

  return (
    <div className="ag-rich-editor">
      <div className="ag-rich-toolbar">
        <button type="button" onClick={() => execCmd("bold")} title="Gras">
          <Bold className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => execCmd("italic")} title="Italique">
          <Italic className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => execCmd("underline")} title="Souligné">
          <Underline className="h-3.5 w-3.5" />
        </button>
        <span className="ag-toolbar-sep" />
        <button type="button" onClick={() => execCmd("insertUnorderedList")} title="Liste à puces">
          <List className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => execCmd("insertOrderedList")} title="Liste numérotée">
          <ListOrdered className="h-3.5 w-3.5" />
        </button>
        <span className="ag-toolbar-sep" />
        <button type="button" onClick={insertLink} title="Insérer un lien">
          <Link2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <div
        ref={editorRef}
        className="ag-rich-body"
        contentEditable
        onInput={handleInput}
        data-placeholder="Rédigez votre feedback ici..."
      />
    </div>
  );
}

const GRADE_FONT_LINK = "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@700;800&display=swap";

// Inject font
if (typeof document !== "undefined" && !document.getElementById("grade-font-link")) {
  const link = document.createElement("link");
  link.id = "grade-font-link";
  link.rel = "stylesheet";
  link.href = GRADE_FONT_LINK;
  document.head.appendChild(link);
}

export default function AssignmentDetailPage() {
  const { workspaceId, assignmentId } = useParams();
  const navigate = useNavigate();
  const { user, canManage } = useAuth();
  const fileInputRef = useRef(null);

  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [activeFile, setActiveFile] = useState(null);
  const [comment, setComment] = useState("");
  const [grading, setGrading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // View state: 'overview' | 'submissions' | 'grading'
  const [view, setView] = useState("overview");

  // Grading view state
  const [gradingIndex, setGradingIndex] = useState(0);
  const [gradeScore, setGradeScore] = useState("");
  const [adminComment, setAdminComment] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const [gradeFeedback, setGradeFeedback] = useState("");
  const [notifyStudent, setNotifyStudent] = useState(true);

  // AI Analysis
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  // Submissions table filters
  const [searchQuery, setSearchQuery] = useState("");
  const [letterFilter, setLetterFilter] = useState("Tout");
  const [lastNameFilter, setLastNameFilter] = useState("Tout");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    loadData();
  }, [assignmentId]);

  const loadData = async () => {
    try {
      const [assignmentRes, submissionsRes] = await Promise.all([
        assignmentAPI.get(workspaceId, assignmentId),
        submissionAPI.list(assignmentId),
      ]);
      setAssignment(assignmentRes.data);
      setSubmissions(submissionsRes.data);

      if (canManage) {
        try {
          const statsRes = await submissionAPI.getStats(workspaceId, assignmentId);
          setStats(statsRes.data);
        } catch (e) { /* stats optional */ }
      }
    } catch (error) {
      console.error("Failed to load assignment:", error);
      toast.error("Erreur lors du chargement");
      navigate(`/workspace/${workspaceId}/assignments`);
    } finally {
      setLoading(false);
    }
  };

  // Candidate submit handler
  const handleSubmit = async () => {
    if (selectedFiles.length === 0) { toast.error("Veuillez sélectionner au moins un fichier"); return; }
    setSubmitting(true);
    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append("file", file);
    });
    if (comment) formData.append("comment", comment);
    try {
      const response = await submissionAPI.submit(assignmentId, formData);
      setSubmissions([...submissions, response.data]);
      setSelectedFiles([]);
      setComment("");
      toast.success("Devoir soumis avec succès");
    } catch (error) {
      toast.error("Erreur lors de la soumission");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete submission (candidate)
  const handleDeleteSubmission = async (subId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer votre soumission ? Vous pourrez en soumettre une nouvelle.")) return;
    setDeleting(true);
    try {
      await submissionAPI.delete(assignmentId, subId);
      setSubmissions(prev => prev.filter(s => s.id !== subId));
      toast.success("Soumission supprimée. Vous pouvez maintenant soumettre à nouveau.");
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors de la suppression";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  // Trainer delete submission
  const handleTrainerDeleteSubmission = async (subId, userName) => {
    if (!window.confirm(`Supprimer la soumission de ${userName} ? Cette action est irréversible.`)) return;
    try {
      await submissionAPI.delete(assignmentId, subId);
      setSubmissions(prev => prev.filter(s => s.id !== subId));
      toast.success("Soumission supprimée");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Grade submission
  const handleGrade = async (subId, score, feedback, goNext = false) => {
    if (score === "" || score === null) { toast.error("Veuillez saisir une note"); return; }
    setGrading(true);
    try {
      await submissionAPI.grade(assignmentId, subId, {
        score: parseInt(score),
        feedback: feedback || "",
        admin_comment: adminComment || "",
      });
      setSubmissions(prev => prev.map(s => s.id === subId ? {
        ...s, score: parseInt(score), feedback, admin_comment: adminComment || '', graded_at: new Date().toISOString()
      } : s));
      toast.success("Note enregistrée");
      if (goNext && gradingIndex < filteredSubmissions.length - 1) {
        openGrading(gradingIndex + 1);
      }
    } catch (error) {
      toast.error("Erreur lors de la notation");
    } finally {
      setGrading(false);
    }
  };

  // AI Analysis handler
  const handleAiAnalyze = async () => {
    if (!currentSub) return;
    setAiAnalyzing(true);
    setAiResult(null);
    try {
      const res = await submissionAPI.aiAnalyze(assignmentId, currentSub.id);
      setAiResult(res.data);
      toast.success("Analyse IA terminée");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'analyse IA");
    } finally {
      setAiAnalyzing(false);
    }
  };

  // Filtered submissions for table
  const filteredSubmissions = useMemo(() => {
    let result = [...submissions];
    if (letterFilter !== "Tout") {
      result = result.filter(s => {
        const name = (s.user_first_name || s.user_name || "").trim();
        return name.charAt(0).toUpperCase() === letterFilter;
      });
    }
    if (lastNameFilter !== "Tout") {
      result = result.filter(s => {
        const name = (s.user_last_name || "").trim();
        return name.charAt(0).toUpperCase() === lastNameFilter;
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s =>
        (s.user_name || "").toLowerCase().includes(q) ||
        (s.user_email || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [submissions, letterFilter, lastNameFilter, searchQuery]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredSubmissions.length / ITEMS_PER_PAGE));
  const paginatedSubmissions = filteredSubmissions.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Open grading view
  const openGrading = (index) => {
    const sub = filteredSubmissions[index];
    if (!sub) return;
    setGradingIndex(index);
    setGradeScore(sub.score !== null && sub.score !== undefined ? sub.score : "");
    setGradeFeedback(sub.feedback || "");
    setAdminComment(sub.admin_comment || "");
    setAiResult(null);
    setView("grading");
  };

  const getStatus = () => {
    if (!assignment) return null;
    const now = new Date();
    const startDate = new Date(assignment.start_date);
    const dueDate = new Date(assignment.due_date);
    if (now < startDate) return { label: "À venir", variant: "secondary" };
    if (now > dueDate) return { label: "Terminé", variant: "outline" };
    const daysLeft = getDaysUntil(assignment.due_date);
    if (daysLeft <= 3) return { label: `Urgent - ${daysLeft}j`, variant: "destructive" };
    return { label: "En cours", variant: "default" };
  };

  const userSubmission = submissions.find(s => s.user_id === user?.id);

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  const status = getStatus();
  const currentSub = filteredSubmissions[gradingIndex];

  // ========================== TRAINER VIEW ==========================
  if (canManage) {
    return (
      <WorkspaceLayout>
        <div className="page-container animate-fade-in">

          {/* ===== OVERVIEW VIEW ===== */}
          {view === "overview" && (
            <>
              {/* Header */}
              <div className="flex items-center gap-4 mb-6">
                <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/assignments`)}>
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-semibold tracking-tight">{assignment?.title}</h1>
                    {status && <Badge variant={status.variant}>{status.label}</Badge>}
                    {assignment?.is_visible === false && (
                      <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                        <EyeOff className="h-3 w-3 mr-1" /> Caché
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-muted-foreground">{assignment?.description}</p>
                </div>
                <Button variant="outline" onClick={() => navigate(`/workspace/${workspaceId}/assignments/${assignmentId}/edit`)}>
                  Modifier le devoir
                </Button>
              </div>

              {/* Assignment Info */}
              <Card className="mb-6">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center gap-6 text-sm text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-4 w-4" />
                      Ouvert le : <strong className="text-foreground">{formatDateTime(assignment?.start_date)}</strong>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-4 w-4" />
                      À rendre : <strong className="text-foreground">{formatDateTime(assignment?.due_date)}</strong>
                    </span>
                  </div>
                  {assignment?.instructions && (
                    <div className="pt-2 border-t">
                      <p className="text-sm text-muted-foreground mb-2">Instructions :</p>
                      <div className="prose prose-slate max-w-none text-sm" dangerouslySetInnerHTML={{ __html: assignment.instructions }} />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 mb-6">
                <Button onClick={() => setView("submissions")} className="gap-2 ag-btn-green">
                  <ClipboardList className="h-4 w-4" />
                  CONSULTER TOUS LES TRAVAUX REMIS
                </Button>
                {submissions.filter(s => s.score === null || s.score === undefined).length > 0 && (
                  <Button onClick={() => {
                    const firstUngraded = submissions.findIndex(s => s.score === null || s.score === undefined);
                    if (firstUngraded >= 0) openGrading(firstUngraded);
                    else setView("submissions");
                  }} className="gap-2 ag-btn-dark">
                    <FileCheck className="h-4 w-4" />
                    ÉVALUER
                  </Button>
                )}
              </div>

              {/* Stats Summary Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Résumé de l'évaluation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="ag-stats-table">
                    <table>
                      <tbody>
                        <tr>
                          <td className="ag-stats-label">Caché pour les étudiants</td>
                          <td>{assignment?.is_visible === false ? "Oui" : "Non"}</td>
                        </tr>
                        <tr>
                          <td className="ag-stats-label">Participants</td>
                          <td>{stats?.total_participants ?? "—"}</td>
                        </tr>
                        <tr>
                          <td className="ag-stats-label">Remis</td>
                          <td>
                            <span className="font-semibold">{stats?.submitted_count ?? submissions.length}</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="ag-stats-label">Nécessitant évaluation</td>
                          <td>
                            <span className={`font-semibold ${(stats?.needs_grading || 0) > 0 ? "text-orange-600" : "text-green-600"}`}>
                              {stats?.needs_grading ?? submissions.filter(s => s.score === null).length}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="ag-stats-label">Notés</td>
                          <td>
                            <span className="font-semibold text-blue-600">
                              {stats?.graded_count ?? submissions.filter(s => s.score !== null).length}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="ag-stats-label">Temps restant</td>
                          <td>{stats?.time_remaining ?? "—"}</td>
                        </tr>
                        <tr>
                          <td className="ag-stats-label">Note maximale</td>
                          <td>{assignment?.max_score} points</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* ===== SUBMISSIONS TABLE VIEW ===== */}
          {view === "submissions" && (
            <>
              {/* Header */}
              <div className="flex items-center gap-4 mb-5">
                <Button variant="ghost" size="icon" onClick={() => setView("overview")}>
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex-1">
                  <h1 className="text-2xl font-semibold tracking-tight">Travaux remis</h1>
                  <p className="text-muted-foreground text-sm">{assignment?.title}</p>
                </div>
              </div>

              {/* Filters */}
              <div className="space-y-2 mb-5">
                {/* Search */}
                <div className="relative max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher par nom ou email..."
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    className="pl-9"
                  />
                </div>

                {/* Alphabet filters */}
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-xs text-muted-foreground mr-1 w-16">Prénom</span>
                  {["Tout", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"].map(letter => (
                    <button
                      key={`fn-${letter}`}
                      onClick={() => { setLetterFilter(letter); setCurrentPage(1); }}
                      className={`ag-letter-btn ${letterFilter === letter ? "active" : ""}`}
                    >
                      {letter}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-xs text-muted-foreground mr-1 w-16">Nom</span>
                  {["Tout", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"].map(letter => (
                    <button
                      key={`ln-${letter}`}
                      onClick={() => { setLastNameFilter(letter); setCurrentPage(1); }}
                      className={`ag-letter-btn ${lastNameFilter === letter ? "active" : ""}`}
                    >
                      {letter}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-1 mb-4">
                  <button className="ag-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</button>
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button key={page} onClick={() => setCurrentPage(page)}
                        className={`ag-page-btn ${currentPage === page ? "active" : ""}`}>
                        {page}
                      </button>
                    );
                  })}
                  {totalPages > 10 && <span className="text-xs text-muted-foreground mx-1">… {totalPages}</span>}
                  <button className="ag-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>›</button>
                </div>
              )}

              {/* Table */}
              <div className="ag-submissions-table-wrap">
                <table className="ag-submissions-table">
                  <thead>
                    <tr>
                      <th style={{ width: "30px" }}>
                        <input type="checkbox" className="ag-checkbox" />
                      </th>
                      <th style={{ width: "50px" }}>Avatar</th>
                      <th>Prénom / Nom de famille</th>
                      <th>Adresse de courriel</th>
                      <th>Statut</th>
                      <th>Note</th>
                      <th>Modifier</th>
                      <th>Dernière modification (travail remis)</th>
                      <th>Remises de fichiers</th>
                      <th>Commentaires</th>
                      <th>Feedback par commentaires</th>
                      <th style={{ width: "60px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSubmissions.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="text-center text-muted-foreground py-8">
                          Aucune soumission trouvée
                        </td>
                      </tr>
                    ) : (
                      paginatedSubmissions.map((sub) => {
                        const globalIdx = filteredSubmissions.indexOf(sub);
                        const initials = (sub.user_name || "?").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
                        const isGraded = sub.score !== null && sub.score !== undefined;

                        let statusLabel, statusCls;
                        if (isGraded && sub.is_late) {
                          statusLabel = "Remis pour évaluation"; statusCls = "ag-status-submitted";
                        } else if (isGraded) {
                          statusLabel = "Remis pour évaluation"; statusCls = "ag-status-submitted";
                        } else if (sub.is_late) {
                          statusLabel = "Pas de travail remis"; statusCls = "ag-status-none";
                        } else {
                          statusLabel = "Pas de travail remis"; statusCls = "ag-status-none";
                        }

                        // Moodle status display
                        const hasFile = !!sub.file_url || (sub.files && sub.files.length > 0);
                        const moodleStatus = hasFile
                          ? (isGraded ? "Remis pour évaluation" : "Remis pour évaluation")
                          : "Pas de travail remis";
                        const moodleCls = hasFile ? "ag-status-submitted" : "ag-status-none";

                        return (
                          <tr key={sub.id}>
                            <td><input type="checkbox" className="ag-checkbox" /></td>
                            <td>
                              <div className="ag-avatar" title={sub.user_name}>
                                {initials}
                              </div>
                            </td>
                            <td>
                              <div>
                                <span style={{ fontWeight: 700, fontSize: 13 }}>{sub.user_first_name || sub.user_name?.split(' ')[0] || "—"}</span>
                                <span style={{ fontWeight: 700, fontSize: 13, marginLeft: 4 }}>{sub.user_last_name || sub.user_name?.split(' ').slice(1).join(' ') || ""}</span>
                              </div>
                            </td>
                            <td>
                              <span style={{ fontWeight: 600, fontSize: 13 }}>{sub.user_email || "—"}</span>
                            </td>
                            <td>
                              <span className={`ag-status-badge ${moodleCls}`}>
                                {moodleStatus}
                              </span>
                              {sub.is_late && sub.late_text && (
                                <span className="ag-late-text">{sub.late_text}</span>
                              )}
                              {isGraded && <span className="ag-graded-sub">Noté</span>}
                            </td>
                            <td>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                                <Button size="sm" className="ag-evaluate-btn" onClick={() => openGrading(globalIdx)}>
                                  ÉVALUER
                                </Button>
                                {isGraded && (() => {
                                  const pct = (sub.score / (assignment.max_score || 1)) * 100;
                                  const badgeColor = pct >= 80 ? "#10b981" : pct >= 60 ? "#3b82f6" : pct >= 50 ? "#f59e0b" : "#ef4444";
                                  const badgeLabel = pct >= 80 ? "Excellent" : pct >= 60 ? "Bien" : pct >= 50 ? "Passable" : "Insuffisant";
                                  const badgeBg = pct >= 80 ? "rgba(16,185,129,0.12)" : pct >= 60 ? "rgba(59,130,246,0.12)" : pct >= 50 ? "rgba(245,158,11,0.12)" : "rgba(239,68,68,0.12)";
                                  return (
                                    <div style={{ textAlign: "center" }}>
                                      <div style={{ fontFamily: "'JetBrains Mono', 'Fira Code', monospace", fontSize: 16, fontWeight: 800, color: badgeColor, letterSpacing: -0.5 }}>
                                        {sub.score} / {assignment.max_score}
                                      </div>
                                      <span style={{ display: "inline-block", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
                                        padding: "2px 8px", borderRadius: 20, marginTop: 2, color: badgeColor, background: badgeBg, border: `1px solid ${badgeColor}30` }}>
                                        {badgeLabel}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </div>
                            </td>
                            <td>
                              <button className="ag-modify-link" onClick={() => openGrading(globalIdx)}>
                                Modifier
                              </button>
                            </td>
                            <td>
                              <span style={{ fontWeight: 600, fontSize: 12.5 }}>
                                {sub.submitted_at ? formatDateTime(sub.submitted_at) : "—"}
                              </span>
                            </td>
                            <td>
                              {sub.files && sub.files.length > 0 ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                  {sub.files.map((file, idx) => (
                                    <a key={idx} href={`${BACKEND_URL}${file.file_url}`} target="_blank" rel="noopener noreferrer"
                                      className="ag-file-link" title={file.file_name || `Fichier ${idx + 1}`}
                                      style={{ fontWeight: 700, fontSize: 12.5, display: "flex", alignItems: "center", gap: 4 }}>
                                      <FileText className="h-3.5 w-3.5 text-purple-600 shrink-0" />
                                      <span className="truncate" style={{ maxWidth: "120px" }}>
                                        {file.file_name || `Fichier ${idx + 1}`}
                                      </span>
                                    </a>
                                  ))}
                                </div>
                              ) : sub.file_url ? (
                                <a href={`${BACKEND_URL}${sub.file_url}`} target="_blank" rel="noopener noreferrer"
                                  className="ag-file-link" title={sub.file_name || "Fichier"}
                                  style={{ fontWeight: 700, fontSize: 12.5, display: "flex", alignItems: "center", gap: 4 }}>
                                  <FileText className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                                  <span className="truncate" style={{ maxWidth: "120px" }}>
                                    {sub.file_name || "fichier"}
                                  </span>
                                </a>
                              ) : "—"}
                            </td>
                            <td>
                              {sub.admin_comment && (
                                <p style={{ fontSize: 11, color: "#10b981", fontWeight: 500, marginBottom: 4, lineHeight: 1.4, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                  {sub.admin_comment}
                                </p>
                              )}
                              {sub.comment && (
                                <p style={{ fontSize: 10, color: "var(--muted-foreground)", fontStyle: "italic", lineHeight: 1.4, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                  Candidat: “{sub.comment}”
                                </p>
                              )}
                              {!sub.admin_comment && !sub.comment && (
                                <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>—</span>
                              )}
                            </td>
                            <td>
                              {sub.feedback ? (() => {
                                const tmp = document.createElement("div");
                                tmp.innerHTML = sub.feedback;
                                const text = tmp.textContent || tmp.innerText || "";
                                return (
                                  <span style={{ fontSize: 11, fontWeight: 500, display: "block", lineHeight: 1.4, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                    {text}
                                  </span>
                                );
                              })() : "—"}
                            </td>
                            <td>
                              <button
                                className="ag-delete-btn"
                                onClick={() => handleTrainerDeleteSubmission(sub.id, sub.user_name)}
                                title="Supprimer cette soumission"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ===== GRADING VIEW (Moodle-like) ===== */}
          {view === "grading" && currentSub && (
            <div className="ag-grading-fullpage">
              {/* Top bar */}
              <div className="ag-grading-topbar">
                <div className="ag-grading-topbar-left">
                  <button onClick={() => setView("submissions")} className="ag-back-link">
                    <ArrowLeft className="h-4 w-4" />
                    Consulter tous les travaux remis
                  </button>
                  <span className="text-sm text-muted-foreground ml-4">
                    Devoir : {assignment?.title}
                  </span>
                </div>
                <div className="ag-grading-topbar-right">
                  <div className="ag-grading-user-info">
                    <div className="ag-avatar-lg">
                      {(currentSub.user_name || "?").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{currentSub.user_name}</p>
                      <p className="text-xs text-muted-foreground">{currentSub.user_email || "—"}</p>
                      <p className="text-xs text-muted-foreground">
                        Date limite : {formatDateTime(assignment?.due_date)}
                      </p>
                    </div>
                  </div>
                  {/* Navigation */}
                  <div className="ag-grading-nav">
                    <span className="text-xs text-muted-foreground">
                      Page {gradingIndex + 1} sur {filteredSubmissions.length}
                    </span>
                    <div className="flex gap-1">
                      <button className="ag-nav-btn" disabled={gradingIndex <= 0} onClick={() => openGrading(gradingIndex - 1)}>◀</button>
                      <button className="ag-nav-btn" disabled={gradingIndex >= filteredSubmissions.length - 1} onClick={() => openGrading(gradingIndex + 1)}>▶</button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main grading layout */}
              <div className="ag-grading-layout">
                {/* Left — File Preview */}
                <div className="ag-grading-preview">
                  {activeFile ? (
                    <>
                      {(activeFile.file_url.toLowerCase().endsWith(".pdf") || activeFile.file_name?.toLowerCase().endsWith(".pdf")) ? (
                        <iframe
                          src={`${BACKEND_URL}${activeFile.file_url}`}
                          className="ag-pdf-iframe"
                          title="Prévisualisation du fichier"
                        />
                      ) : (activeFile.file_url.toLowerCase().endsWith(".docx") || activeFile.file_url.toLowerCase().endsWith(".doc") || activeFile.file_name?.toLowerCase().endsWith(".docx") || activeFile.file_name?.toLowerCase().endsWith(".doc")) ? (
                        <iframe
                          src={`${BACKEND_URL}/api/files/preview-docx/${activeFile.file_url.split("/").pop()}`}
                          className="ag-pdf-iframe"
                          title="Prévisualisation DOCX"
                          style={{ background: '#fff' }}
                        />
                      ) : (
                        <div className="ag-no-preview">
                          <FileText className="h-12 w-12 text-muted-foreground mb-3" />
                          <p className="font-medium">Aperçu non disponible</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Ce type de fichier ne peut pas être prévisualisé.
                          </p>
                          <a href={`${BACKEND_URL}${activeFile.file_url}`} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" className="mt-4 gap-2">
                              <Download className="h-4 w-4" /> Télécharger le fichier
                            </Button>
                          </a>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="ag-no-preview">
                      <XCircle className="h-12 w-12 text-muted-foreground mb-3" />
                      <p className="font-medium">Aucun fichier soumis</p>
                    </div>
                  )}
                </div>

                {/* Right — Grading Panel (Moodle-style) */}
                <div className="ag-grading-panel" style={{ position: "relative", zIndex: 5 }}>
                  {/* Travail remis section */}
                  <div className="ag-panel-section">
                    <h3 className="ag-panel-title-green">Travail remis</h3>
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        {(currentSub.file_url || (currentSub.files && currentSub.files.length > 0)) ? (
                          <span className="ag-moodle-status green">Remis pour évaluation</span>
                        ) : (
                          <span className="ag-moodle-status red">Pas de travail remis</span>
                        )}
                        {currentSub.is_late && (
                          <span className="ag-moodle-status orange">En retard</span>
                        )}
                        {currentSub.score !== null && currentSub.score !== undefined && (
                          <span className="ag-moodle-status blue">Noté</span>
                        )}
                      </div>
                      {currentSub.is_late && currentSub.late_text && (
                        <p className="text-xs" style={{ color: "#dc2626" }}>{currentSub.late_text}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        L'étudiant peut modifier ce travail remis.
                      </p>
                    </div>
                  </div>

                  {/* Files section */}
                  {(currentSub.files && currentSub.files.length > 0) ? (
                    <div className="ag-panel-section animate-fade-in">
                      <h4 className="text-xs font-semibold text-muted-foreground mb-2">Fichiers téléversés ({currentSub.files.length}) :</h4>
                      <div className="space-y-1.5">
                        {currentSub.files.map((file, idx) => {
                          const isActive = activeFile?.file_url === file.file_url;
                          return (
                            <div 
                              key={idx}
                              onClick={() => setActiveFile(file)}
                              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all border ${
                                isActive 
                                  ? "bg-purple-50/70 border-purple-300 shadow-sm" 
                                  : "border-transparent hover:bg-slate-50 hover:border-slate-100"
                              }`}
                            >
                              <FileText className={`h-4 w-4 shrink-0 ${isActive ? "text-purple-600" : "text-slate-400"}`} />
                              <div className="flex-1 min-w-0">
                                <span className={`text-xs block truncate ${isActive ? "font-bold text-purple-900" : "font-medium"}`}>
                                  {file.file_name || `Fichier ${idx + 1}`}
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                  {currentSub.submitted_at ? formatDateTime(currentSub.submitted_at) : ""}
                                </span>
                              </div>
                              <a 
                                href={`${BACKEND_URL}${file.file_url}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-slate-400 hover:text-purple-600 p-1 rounded hover:bg-slate-100/80 transition-colors"
                                title="Télécharger"
                              >
                                <Download className="h-3 w-3" />
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : currentSub.file_url ? (
                    <div className="ag-panel-section">
                      <h4 className="text-xs font-semibold text-muted-foreground mb-2">Fichier téléversé (1) :</h4>
                      <div 
                        onClick={() => setActiveFile({ file_url: currentSub.file_url, file_name: currentSub.file_name })}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all border ${
                          activeFile?.file_url === currentSub.file_url 
                            ? "bg-purple-50/70 border-purple-300 shadow-sm" 
                            : "border-transparent hover:bg-slate-50 hover:border-slate-100"
                        }`}
                      >
                        <FileText className={`h-4 w-4 shrink-0 ${activeFile?.file_url === currentSub.file_url ? "text-purple-600" : "text-slate-400"}`} />
                        <div className="flex-1 min-w-0">
                          <span className={`text-xs block truncate ${activeFile?.file_url === currentSub.file_url ? "font-bold text-purple-900" : "font-medium"}`}>
                            {currentSub.file_name || "Fichier soumis"}
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            {currentSub.submitted_at ? formatDateTime(currentSub.submitted_at) : ""}
                          </span>
                        </div>
                        <a 
                          href={`${BACKEND_URL}${currentSub.file_url}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-slate-400 hover:text-purple-600 p-1 rounded hover:bg-slate-100/80 transition-colors"
                          title="Télécharger"
                        >
                          <Download className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  ) : null}

                  {/* Comments section */}
                  <div className="ag-panel-section">
                    <details open>
                      <summary className="ag-panel-summary" style={{ fontWeight: 700 }}>
                        💬 Commentaires
                      </summary>
                      {/* Student comment */}
                      {currentSub.comment && (
                        <div style={{ marginTop: 8, padding: "8px 12px", background: "var(--muted)", borderRadius: 10, borderLeft: "3px solid #6366f1" }}>
                          <p style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", marginBottom: 2, textTransform: "uppercase" }}>Commentaire du candidat</p>
                          <p style={{ fontSize: 13, color: "var(--foreground)" }}>"{currentSub.comment}"</p>
                        </div>
                      )}
                      {/* Admin comment - editable */}
                      <div style={{ marginTop: 10 }}>
                        <p style={{ fontSize: 10, fontWeight: 700, color: "#10b981", marginBottom: 4, textTransform: "uppercase" }}>Votre commentaire</p>
                        <Textarea
                          value={adminComment}
                          onChange={(e) => setAdminComment(e.target.value)}
                          placeholder="Ajoutez un commentaire pour ce candidat..."
                          rows={3}
                          className="mt-1"
                          style={{ position: "relative", zIndex: 10 }}
                        />
                        <p style={{ fontSize: 10, color: "var(--muted-foreground)", marginTop: 4 }}>
                          💡 Ce commentaire sera enregistré avec la note
                        </p>
                      </div>
                    </details>
                  </div>

                  {/* Note section */}
                  <div className="ag-panel-section">
                    <h3 className="ag-panel-title-green">Note</h3>
                    <Label className="text-xs text-muted-foreground">Note sur {assignment?.max_score}</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        min="0"
                        max={assignment?.max_score}
                        value={gradeScore}
                        onChange={e => setGradeScore(e.target.value)}
                        className="w-24"
                        placeholder="0"
                      />
                      <span className="text-sm text-muted-foreground">/ {assignment?.max_score}</span>
                      {gradeScore !== "" && (
                        <div className="ag-score-indicator" data-passing={((parseInt(gradeScore) || 0) / (assignment?.max_score || 100)) >= 0.5 ? "true" : "false"}>
                          ●
                        </div>
                      )}
                    </div>
                    {currentSub.score !== null && currentSub.score !== undefined && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Note actuelle dans le carnet de notes : <strong>{currentSub.score},00</strong>
                      </p>
                    )}
                    {/* Score bar */}
                    {gradeScore !== "" && (
                      <div className="ag-score-bar mt-2">
                        <div
                          className="ag-score-fill"
                          style={{
                            width: `${Math.min(100, ((parseInt(gradeScore) || 0) / (assignment?.max_score || 100)) * 100)}%`,
                            backgroundColor: ((parseInt(gradeScore) || 0) / (assignment?.max_score || 100)) >= 0.5 ? "#22c55e" : "#ef4444"
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Feedback section with rich editor */}
                  <div className="ag-panel-section ag-panel-section-grow">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="ag-panel-title-green" style={{ marginBottom: 0 }}>Feedback par commentaires</h3>
                    </div>
                    <FeedbackEditor
                      key={currentSub.id}
                      value={gradeFeedback}
                      onChange={setGradeFeedback}
                    />
                  </div>

                  {/* Bottom action bar */}
                  <div className="ag-grading-bottom-bar">
                    <label className="ag-notify-check">
                      <input type="checkbox" checked={notifyStudent} onChange={e => setNotifyStudent(e.target.checked)} />
                      <Bell className="h-3.5 w-3.5" />
                      Notifier l'étudiant
                    </label>
                    <div className="ag-grading-bottom-actions">
                      <Button
                        onClick={() => handleGrade(currentSub.id, gradeScore, gradeFeedback, false)}
                        disabled={grading}
                        className="ag-btn-green gap-1.5"
                        size="sm"
                      >
                        {grading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                        ENREGISTRER
                      </Button>
                      <Button
                        onClick={() => handleGrade(currentSub.id, gradeScore, gradeFeedback, true)}
                        disabled={grading || gradingIndex >= filteredSubmissions.length - 1}
                        className="ag-btn-green gap-1.5"
                        size="sm"
                      >
                        <SkipForward className="h-3.5 w-3.5" />
                        ENREGISTRER ET AFFICHER LA SUITE
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setGradeScore(currentSub.score ?? "");
                          setGradeFeedback(currentSub.feedback || "");
                        }}
                        className="gap-1.5"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        RÉINITIALISER
                      </Button>
                      {canManage && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAiAnalyze}
                          disabled={aiAnalyzing || (!currentSub?.file_url && (!currentSub?.files || currentSub.files.length === 0))}
                          className="gap-1.5 border-purple-300 text-purple-700 hover:bg-purple-50"
                        >
                          {aiAnalyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                          {aiAnalyzing ? "Analyse en cours..." : "🤖 Analyse IA"}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* AI Analysis Result Panel */}
                  {aiResult && (
                    <div style={{ margin: '12px 0', padding: 16, borderRadius: 12, background: 'linear-gradient(135deg, rgba(147,51,234,0.05), rgba(79,70,229,0.05))', border: '1px solid rgba(147,51,234,0.2)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Sparkles style={{ width: 18, height: 18, color: '#7c3aed' }} />
                          <h4 style={{ fontWeight: 600, fontSize: '0.9rem', color: '#7c3aed' }}>Analyse IA</h4>
                          {aiResult.confidence !== undefined && (
                            <Badge variant="outline" style={{ fontSize: 10, color: aiResult.confidence >= 0.7 ? '#15803d' : '#b45309' }}>
                              Confiance: {Math.round(aiResult.confidence * 100)}%
                            </Badge>
                          )}
                        </div>
                        <button onClick={() => setAiResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9ca3af' }}>&times;</button>
                      </div>

                      {/* AI Score */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, padding: '10px 14px', borderRadius: 8, background: 'white', border: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: (aiResult.score / (assignment?.max_score || 100)) >= 0.5 ? '#22c55e' : '#ef4444' }}>
                          {aiResult.score}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>/ {assignment?.max_score}</div>
                        <div style={{ flex: 1, height: 8, borderRadius: 4, background: '#f3f4f6', overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: 4, width: `${Math.min(100, (aiResult.score / (assignment?.max_score || 100)) * 100)}%`, background: (aiResult.score / (assignment?.max_score || 100)) >= 0.5 ? '#22c55e' : '#ef4444', transition: 'width 0.5s ease' }} />
                        </div>
                      </div>

                      {/* AI Criteria */}
                      {aiResult.criteria_scores && Object.keys(aiResult.criteria_scores).length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 12 }}>
                          {Object.entries(aiResult.criteria_scores).map(([key, val]) => (
                            <div key={key} style={{ padding: '8px 10px', borderRadius: 8, background: 'white', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                              <div style={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'capitalize', marginBottom: 2 }}>
                                {key === 'comprehension' ? 'Compréhension' : key === 'completeness' ? 'Complétude' : key === 'technical_quality' ? 'Qualité technique' : key === 'presentation' ? 'Présentation' : key}
                              </div>
                              <div style={{ fontSize: '1rem', fontWeight: 600, color: val >= 70 ? '#22c55e' : val >= 40 ? '#eab308' : '#ef4444' }}>{val}%</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* AI Feedback */}
                      {aiResult.feedback && (
                        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'white', border: '1px solid #e5e7eb', fontSize: '0.8rem', lineHeight: 1.5, color: '#374151', marginBottom: 12, maxHeight: 150, overflowY: 'auto' }}>
                          {aiResult.feedback}
                        </div>
                      )}

                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                          size="sm"
                          className="gap-1.5"
                          style={{ background: '#7c3aed', color: 'white' }}
                          onClick={() => {
                            setGradeScore(String(aiResult.score));
                            setGradeFeedback(aiResult.feedback || "");
                            toast.success("Note et feedback IA appliqués");
                          }}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Appliquer la note et le feedback
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          onClick={() => {
                            setGradeScore(String(aiResult.score));
                            setGradeFeedback(aiResult.feedback || "");
                            setAiResult(null);
                            toast.info("Feedback IA copié — vous pouvez le modifier");
                          }}
                        >
                          Utiliser comme base
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </WorkspaceLayout>
    );
  }

  // ========================== CANDIDATE VIEW ==========================
  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/assignments`)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-semibold tracking-tight">{assignment?.title}</h1>
              {status && <Badge variant={status.variant}>{status.label}</Badge>}
            </div>
            <p className="mt-1 text-muted-foreground">{assignment?.description}</p>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main */}
          <div className="lg:col-span-2 space-y-6">
            {assignment?.instructions && (
              <Card>
                <CardHeader><CardTitle>Instructions</CardTitle></CardHeader>
                <CardContent>
                  <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: assignment.instructions }} />
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Soumettre votre travail</CardTitle>
                <CardDescription>
                  {userSubmission ? "Vous avez soumis ce devoir" 
                    : (!assignment?.submission_open && assignment?.submission_open !== undefined)
                      ? "La soumission est fermée par le formateur"
                      : (assignment?.due_date && new Date(assignment.due_date) < new Date())
                        ? "La date limite est dépassée"
                        : "Téléversez votre fichier avant la date limite"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Check if submission is blocked */}
                {!userSubmission && (
                  (!assignment?.submission_open && assignment?.submission_open !== undefined) ? (
                    <div className="text-center py-8 space-y-3">
                      <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-950/30 flex items-center justify-center">
                        <XCircle className="h-8 w-8 text-red-500" />
                      </div>
                      <p className="font-semibold text-red-600 dark:text-red-400">Soumission fermée</p>
                      <p className="text-sm text-muted-foreground">Le formateur a désactivé la soumission pour ce devoir</p>
                    </div>
                  ) : (assignment?.due_date && new Date(assignment.due_date) < new Date()) ? (
                    <div className="text-center py-8 space-y-3">
                      <div className="mx-auto w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-950/30 flex items-center justify-center">
                        <Clock className="h-8 w-8 text-orange-500" />
                      </div>
                      <p className="font-semibold text-orange-600 dark:text-orange-400">Date limite dépassée</p>
                      <p className="text-sm text-muted-foreground">La date limite était le {formatDateTime(assignment?.due_date)}</p>
                    </div>
                  ) : null
                )}
                {userSubmission ? (
                  <div className="space-y-4">
                    {/* Submission info */}
                    <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                        <div>
                          <p className="font-medium">Devoir soumis</p>
                          <p className="text-sm text-muted-foreground">Le {formatDateTime(userSubmission.submitted_at)}</p>
                        </div>
                      </div>
                      {/* Delete button - only if not yet graded */}
                      {(userSubmission.score === null || userSubmission.score === undefined) && 
                       (assignment?.submission_open !== false) && 
                       !(assignment?.due_date && new Date(assignment.due_date) < new Date()) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
                          onClick={() => handleDeleteSubmission(userSubmission.id)}
                          disabled={deleting}
                        >
                          {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          Supprimer et resoumettre
                        </Button>
                      )}
                    </div>

                    {/* Files list */}
                    {userSubmission.files && userSubmission.files.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground">Fichiers soumis ({userSubmission.files.length}) :</p>
                        {userSubmission.files.map((file, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border hover:border-purple-200 hover:bg-purple-50/10 transition-colors animate-fade-in">
                            <FileText className="h-5 w-5 text-purple-600 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{file.file_name || `Fichier ${idx + 1}`}</p>
                              <p className="text-xs text-muted-foreground">Soumis le {formatDateTime(userSubmission.submitted_at)}</p>
                            </div>
                            <a href={`${BACKEND_URL}${file.file_url}`} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="sm" className="gap-1 hover:text-purple-600 hover:bg-purple-50">
                                <Download className="h-3.5 w-3.5" /> Télécharger
                              </Button>
                            </a>
                          </div>
                        ))}
                      </div>
                    ) : userSubmission.file_url ? (
                      <div className="flex items-center gap-3 p-3 rounded-lg border">
                        <FileText className="h-5 w-5 text-primary shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{userSubmission.file_name || "Fichier soumis"}</p>
                          <p className="text-xs text-muted-foreground">Soumis le {formatDateTime(userSubmission.submitted_at)}</p>
                        </div>
                        <a href={`${BACKEND_URL}${userSubmission.file_url}`} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm" className="gap-1">
                            <Download className="h-3.5 w-3.5" /> Télécharger
                          </Button>
                        </a>
                      </div>
                    ) : null}

                    {/* Grade info */}
                    {userSubmission.score !== null && userSubmission.score !== undefined && (
                      <div className="p-4 rounded-lg border">
                        <p className="text-sm text-muted-foreground">Votre note</p>
                        <p className="text-2xl font-semibold">{userSubmission.score}/{assignment.max_score}</p>
                        {/* Score bar */}
                        <div className="ag-score-bar mt-2">
                          <div className="ag-score-fill" style={{
                            width: `${(userSubmission.score / assignment.max_score) * 100}%`,
                            backgroundColor: (userSubmission.score / assignment.max_score) >= 0.5 ? "#22c55e" : "#ef4444"
                          }} />
                        </div>
                        {userSubmission.feedback && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-sm text-muted-foreground">Feedback du formateur</p>
                            <div className="mt-1 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: userSubmission.feedback }} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (assignment?.submission_open !== false && !(assignment?.due_date && new Date(assignment.due_date) < new Date())) ? (
                  <div className="space-y-4">
                    <div
                      className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-slate-50/50 transition-all"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                      <p className="mt-2 font-medium">
                        {selectedFiles.length > 0 
                          ? `${selectedFiles.length} fichier(s) sélectionné(s)` 
                          : "Cliquez pour sélectionner un ou plusieurs fichiers"
                        }
                      </p>
                      <p className="text-sm text-muted-foreground">Formats acceptés: PDF, Word (.doc, .docx)</p>
                      <p className="text-xs text-muted-foreground mt-1">Taille maximale: 50 MB</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="hidden"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          const validFiles = [];
                          for (const file of files) {
                            if (file.size > 50 * 1024 * 1024) {
                              toast.error(`Le fichier ${file.name} est trop volumineux (max 50 Mo)`);
                              continue;
                            }
                            // Check for duplicates
                            if (selectedFiles.some(f => f.name === file.name && f.size === file.size)) {
                              toast.info(`Le fichier ${file.name} est déjà sélectionné`);
                              continue;
                            }
                            validFiles.push(file);
                          }
                          if (validFiles.length > 0) {
                            setSelectedFiles(prev => [...prev, ...validFiles]);
                          }
                        }}
                      />
                    </div>
                    {selectedFiles.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground">Fichiers sélectionnés :</p>
                        {selectedFiles.map((file, idx) => (
                          <div key={`${file.name}-${idx}`} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-slate-100 hover:border-purple-200 transition-all animate-fade-in">
                            <FileText className="h-5 w-5 text-purple-600 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{file.name}</p>
                              <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} Mo</p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Commentaire (optionnel)</Label>
                      <Textarea placeholder="Ajoutez un commentaire..." value={comment} onChange={e => setComment(e.target.value)} rows={3} />
                    </div>
                    <Button onClick={handleSubmit} disabled={selectedFiles.length === 0 || submitting} className="w-full">
                      {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Soumettre mon travail
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Informations</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Date de début</p>
                    <p className="font-medium">{formatDate(assignment?.start_date)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Date limite</p>
                    <p className="font-medium">{formatDate(assignment?.due_date)}</p>
                    {assignment?.due_date && new Date(assignment.due_date) < new Date() && (
                      <p className="text-xs text-red-500 font-semibold mt-1">⏰ Date limite dépassée</p>
                    )}
                    {assignment?.submission_open === false && (
                      <p className="text-xs text-red-500 font-semibold mt-1">🔒 Soumission fermée</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Note maximale</p>
                    <p className="font-medium">{assignment?.max_score} points</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
}
